import { Button, Heading, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED, isAbortError, pluralize } from "@overmap-ai/common"
import { selectReports } from "@overmap-ai/core"
import { useAbortableCallback, useAbortableEffect } from "@overmap-ai/hooks"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useReportTable, useReportTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/reports/")

export const ReportsView = memo(() => {
	const { organizationId } = routeApi.useParams()
	const reports = useShallowEqaulSelector(selectReports)
	const [isLoading, setIsLoading] = useState(true)
	const [hasLoadError, setHasLoadError] = useState(false)
	const columns = useReportTableColumns()
	const table = useReportTable({ columns, data: reports })

	const fetchReports = useCallback(
		async (signal: AbortSignal) => {
			setIsLoading(true)
			setHasLoadError(false)

			try {
				await sdk.reports.refreshStore(organizationId, signal)
			} catch (e: unknown) {
				if (isAbortError(e) || signal.aborted) return
				toast.error({
					title: "Failed to load reports",
					description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
				})
				setHasLoadError(true)
			} finally {
				if (!signal.aborted) {
					setIsLoading(false)
				}
			}
		},
		[organizationId],
	)

	useAbortableEffect(
		(abortController) => {
			void fetchReports(abortController.signal)
		},
		[fetchReports],
	)

	const handleRefresh = useAbortableCallback(
		(abortController) => {
			void fetchReports(abortController.signal)
		},
		[fetchReports],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Reports</Heading>
				</div>

				<Separator size="full" />

				<div className="flex grow flex-col gap-2">
					<div className="flex items-end justify-between gap-2">
						<Text size="sm" role="alert" accentColor="base">
							{reports.length} {pluralize("report", reports.length)}
						</Text>

						<Button variant="surface" size="sm" onClick={handleRefresh} disabled={isLoading}>
							<LuIcon icon={modelIconMapping.refresh} />
							Refresh
						</Button>
					</div>

					<div className="w-full overflow-auto" aria-busy={isLoading}>
						<Table
							className="w-full"
							table={{ ...table }}
							variant="surface"
							size="sm"
							interactive={false}
							emptyText={
								isLoading
									? "Loading reports…"
									: hasLoadError
										? "Reports could not be loaded."
										: "No reports."
							}
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
ReportsView.displayName = "ReportsView"
